import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { apiErrorMessage, deleteTestWeightage, uploadTestWeightage } from "../../api/assessmentTests";
import { BUTTON_STYLES } from "../../styles/componentStyles";

// A csv names the subject in a column; an .xlsx can instead use one sheet per subject (sheet name = subject name).
export const WEIGHTAGE_TEMPLATE_CSV =
  "Subject,Chapter,Topic,SubTopic,Mock Qs,Qs from PYQ,Qs from New-Assessment\n" +
  "Biology,Plant Growth,Germination,,10,4,6\n" +
  "Biology,Plant Growth,Differentiation,Seed Coat,5,2,3\n" +
  "Chemistry,Acids,pH,,4,1,3\n";

const ACCEPTED_TYPES = ".xlsx,.xls,.csv";

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([WEIGHTAGE_TEMPLATE_CSV], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "test-weightage-template.csv";
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Builds a draft test from an uploaded weightage file (chapter, topic, question count, how many of them from PYQ and
 * from New-Assessment questions). While a weightage exists the test is published from exactly those counts;
 * without one the Test Builder distribution is used.
 *
 * `weightage` is { rows, totalQuestions, pyqQuestions, newQuestions } (rows empty = none). `onChanged` runs after an
 * upload or a removal so the parent can reload the test, its distribution and the weightage.
 */
function TestWeightageCard({ testId, weightage, editable, onChanged }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  const rows = weightage?.rows || [];
  const hasWeightage = rows.length > 0;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // picking the same file again must still fire
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      await uploadTestWeightage(testId, file);
      await onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to upload the weightage"));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError("");
    setRemoving(true);
    try {
      await deleteTestWeightage(testId);
      await onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to remove the weightage"));
    } finally {
      setRemoving(false);
    }
  };

  if (!testId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Create the test first - its distribution can stay empty - and the weightage can then be uploaded here.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {editable && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED_TYPES}
            hidden
            data-testid="weightage-file-input"
            onChange={handleFile}
          />
          <LoadingButton
            size="small"
            loading={uploading}
            disabled={removing}
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => fileInput.current?.click()}
            {...BUTTON_STYLES.primary}
          >
            {hasWeightage ? "Replace weightage" : "Upload weightage"}
          </LoadingButton>
          <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={downloadTemplate} {...BUTTON_STYLES.secondary}>
            Download template
          </Button>
          {hasWeightage && (
            <LoadingButton
              size="small"
              color="error"
              loading={removing}
              disabled={uploading}
              startIcon={<DeleteOutlineIcon />}
              onClick={handleRemove}
            >
              Remove weightage
            </LoadingButton>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
          {error}
        </Alert>
      )}

      {hasWeightage && (
        <>
          <Alert severity="info">
            This test follows the uploaded weightage: questions are picked exactly as listed ({weightage.pyqQuestions}{" "}
            from PYQ and {weightage.newQuestions} from New-Assessment, {weightage.totalQuestions} in total). The
            difficulty split and the distribution below are not used. Remove the weightage to use the Test Builder
            again.
          </Alert>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ "& th": { backgroundColor: "#17479e", color: "white", fontWeight: 600 } }}>
                  <TableCell>Subject</TableCell>
                  <TableCell>Chapter</TableCell>
                  <TableCell>Topic</TableCell>
                  <TableCell>Sub-topic</TableCell>
                  <TableCell align="right">Qs</TableCell>
                  <TableCell align="right">PYQ</TableCell>
                  <TableCell align="right">New-Assessment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.sectionId}>
                    <TableCell>{row.subjectName || "—"}</TableCell>
                    <TableCell>{row.chapterName || "—"}</TableCell>
                    <TableCell>{row.topicName}</TableCell>
                    <TableCell>{row.subTopicName || "—"}</TableCell>
                    <TableCell align="right">{row.questionCount}</TableCell>
                    <TableCell align="right">{row.pyqCount}</TableCell>
                    <TableCell align="right">{row.newCount}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {weightage.totalQuestions}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {weightage.pyqQuestions}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {weightage.newQuestions}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

export default TestWeightageCard;
